import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, supabaseAnonKeyExport } from '../lib/supabase';
import Sidebar from '../components/Sidebar';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://qnqxqxhlxaqbryapqmvd.supabase.co';

const STANDARD_EVENTS = [
  'customer.create', 'customer.update', 'customer.delete',
  'supplier.create', 'supplier.update', 'supplier.delete',
  'account.create',  'account.update',  'account.delete',
  'department.create', 'department.update', 'department.delete',
  'project.create',  'project.update',  'project.delete',
  'employee.create', 'employee.update', 'employee.delete',
  'voucher.create',  'voucher.update',  'voucher.delete',
];

const SYNC_TYPES = [
  { key: 'customers',   label: 'Kunder' },
  { key: 'suppliers',   label: 'Leverandører' },
  { key: 'accounts',    label: 'Kontoer' },
  { key: 'departments', label: 'Avdelinger' },
  { key: 'projects',    label: 'Prosjekter' },
  { key: 'employees',   label: 'Ansatte' },
];

async function callFn(name, { method = 'POST', body } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Ikke innlogget. Logg inn på nytt.');
  }
  let url = `${SUPABASE_URL}/functions/v1/${name}`;
  let fetchBody;

  if (method === 'GET' && body) {
    // GET requests cannot have a body — pass fields as query params instead
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(body).map(([k, v]) => [k, String(v)]))
    );
    url += `?${params.toString()}`;
  } else if (body !== undefined) {
    fetchBody = JSON.stringify(body);
  }

  const apikey = supabaseAnonKeyExport;
  if (!apikey || apikey.startsWith('PLACEHOLDER')) {
    throw new Error('Mangler Supabase anon key. Sett REACT_APP_SUPABASE_ANON_KEY i .env');
  }
  const res = await fetch(url, {
    method,
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': apikey,
      'Content-Type': 'application/json',
    },
    body: fetchBody,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

function StatusDot({ status }) {
  const map = {
    valid:   'bg-emerald-500',
    invalid: 'bg-red-500',
    pending: 'bg-amber-400 animate-pulse',
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${map[status] || 'bg-slate-500'}`} />
  );
}

function StatusLabel({ status, isDark }) {
  const map = {
    valid:   { text: 'Gyldig',       color: 'text-emerald-500' },
    invalid: { text: 'Ugyldig',      color: 'text-red-500' },
    pending: { text: 'Bekrefter…',   color: 'text-amber-400' },
  };
  const { text, color } = map[status] || { text: 'Ukjent', color: isDark ? 'text-slate-500' : 'text-slate-400' };
  return <span className={`font-mono text-xs ${color}`}>{text}</span>;
}

function SyncResult({ result }) {
  if (!result) return null;
  if (result.errors > 0 && result.synced === 0)
    return <span className="font-mono text-[11px] text-red-500">✗ {result.errors} feil</span>;
  if (result.errors > 0)
    return <span className="font-mono text-[11px] text-amber-400">✓ {result.synced} ({result.errors} feil)</span>;
  return <span className="font-mono text-[11px] text-emerald-500">✓ {result.synced}</span>;
}

export default function Enheter() {
  const [isDark, setIsDark] = useState(true);
  const [user, setUser]     = useState(null);
  const navigate            = useNavigate();

  // Data
  const [entities, setEntities]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [subscriptions, setSubscriptions] = useState({}); // { [entityId]: [...] }
  const [entityAccounts, setEntityAccounts] = useState({}); // { [entityId]: [...] }
  const [cashAccountSaving, setCashAccountSaving] = useState({}); // { [entityId]: bool }
  const [cashAccountResult, setCashAccountResult] = useState({}); // { [entityId]: 'ok'|'error' }

  // Per-entity UI state
  const [tokenInput, setTokenInput]       = useState({}); // { [entityId]: string }
  const [savingToken, setSavingToken]     = useState({}); // { [entityId]: bool }
  const [tokenResult, setTokenResult]     = useState({}); // { [entityId]: { valid, error } }

  const [syncState, setSyncState]   = useState({}); // { [entityId]: { [type]: 'idle'|'loading'|'done'|'error' } }
  const [syncResults, setSyncResults] = useState({}); // { [entityId]: { [type]: {synced,errors} } }

  const [addingAllSubs, setAddingAllSubs] = useState({}); // { [entityId]: bool }
  const [addSubsResult, setAddSubsResult] = useState({}); // { [entityId]: { ok, failed } }
  const [deletingSub, setDeletingSub]     = useState({}); // { [entityId+subId]: bool }
  const [loadingSubs, setLoadingSubs]     = useState({}); // { [entityId]: bool }

  const [voucherDateFrom, setVoucherDateFrom] = useState({});
  const [voucherDateTo, setVoucherDateTo]     = useState({});
  const [voucherEnqueuing, setVoucherEnqueuing] = useState({});
  const [voucherQueue, setVoucherQueue]       = useState({});
  const [voucherProcessQueue, setVoucherProcessQueue] = useState({}); // { [entityId]: [...] }

  const bg         = isDark ? 'bg-slate-950 text-slate-50'  : 'bg-slate-50 text-slate-900';
  const card       = isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-slate-200';
  const section    = isDark ? 'border-slate-800' : 'border-slate-200';
  const inputCls   = isDark
    ? 'bg-transparent border-b border-slate-700 focus:border-slate-400 text-slate-100 placeholder-slate-600'
    : 'bg-transparent border-b border-slate-300 focus:border-slate-500 text-slate-900 placeholder-slate-400';
  const btnPrimary = isDark
    ? 'bg-slate-700 hover:bg-slate-600 text-slate-100 border border-slate-600'
    : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700';
  const btnSmall   = isDark
    ? 'bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300'
    : 'bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700';

  // Auth — use functional setUser to stabilize the reference (avoid infinite loop
  // caused by TOKEN_REFRESHED creating a new user object on every refresh)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) navigate('/login', { replace: true });
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      const next = session?.user ?? null;
      setUser(prev => (prev?.id === next?.id ? prev : next));
      if (!session) navigate('/login', { replace: true });
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Load entities
  const loadEntities = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('user_legal_entity_access')
      .select(`
        role,
        legal_entity:legal_entities (
          id, name, organization_number,
          cash_account_tripletex_id, cash_loss_account_tripletex_id,
          tripletex_integration:tripletex_integrations (
            token_status, is_active, last_verified_at, last_error
          )
        )
      `)
      .eq('user_id', user.id);

    if (!error && data) {
      setEntities(data.map(row => ({ ...row.legal_entity, role: row.role })));
    }
    setLoading(false);
  }, [user]);

  // Load synced accounts for each entity
  const loadEntityAccounts = useCallback(async (entityId) => {
    const { data } = await supabase
      .from('tripletex_accounts')
      .select('tripletex_id, number, name')
      .eq('legal_entity_id', entityId)
      .order('number', { ascending: true });
    if (data) {
      setEntityAccounts(prev => ({ ...prev, [entityId]: data }));
    }
  }, []);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  useEffect(() => {
    entities.forEach(e => loadEntityAccounts(e.id));
  }, [entities, loadEntityAccounts]);

  // Load subscriptions directly from Supabase (RLS handles access control)
  const loadSubscriptions = useCallback(async (entityId) => {
    setLoadingSubs(prev => ({ ...prev, [entityId]: true }));
    try {
      const { data, error } = await supabase
        .from('tripletex_subscriptions')
        .select('*')
        .eq('legal_entity_id', entityId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (!error && data) {
        setSubscriptions(prev => ({ ...prev, [entityId]: data }));
      }
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    }
    setLoadingSubs(prev => ({ ...prev, [entityId]: false }));
  }, []);

  useEffect(() => {
    entities.forEach(e => loadSubscriptions(e.id));
  }, [entities, loadSubscriptions]);

  // Load voucher sync queue (day-level) and process queue (voucher-level)
  const loadVoucherQueue = useCallback(async () => {
    if (!entities.length) return;
    const ids = entities.map(e => e.id);

    // Day-level queue
    const { data, error } = await supabase
      .from('voucher_sync_queue')
      .select('*')
      .in('legal_entity_id', ids)
      .order('sync_date', { ascending: true });
    if (!error && data) {
      const grouped = {};
      for (const row of data) {
        if (!grouped[row.legal_entity_id]) grouped[row.legal_entity_id] = [];
        grouped[row.legal_entity_id].push(row);
      }
      setVoucherQueue(grouped);
    }

    // Voucher-level process queue — use head:true count queries to avoid 1000-row limit
    const countsByEntity = {};
    for (const eid of ids) {
      const [queuedRes, processingRes, doneRes, erroredRes, retryingRes] = await Promise.all([
        supabase.from('voucher_process_queue').select('*', { count: 'exact', head: true }).eq('legal_entity_id', eid).eq('status', 'queued'),
        supabase.from('voucher_process_queue').select('*', { count: 'exact', head: true }).eq('legal_entity_id', eid).eq('status', 'processing'),
        supabase.from('voucher_process_queue').select('*', { count: 'exact', head: true }).eq('legal_entity_id', eid).eq('status', 'done'),
        supabase.from('voucher_process_queue').select('*', { count: 'exact', head: true }).eq('legal_entity_id', eid).eq('status', 'error').gte('retry_count', 3),
        supabase.from('voucher_process_queue').select('*', { count: 'exact', head: true }).eq('legal_entity_id', eid).eq('status', 'error').lt('retry_count', 3),
      ]);
      countsByEntity[eid] = {
        queued: queuedRes.count ?? 0,
        processing: processingRes.count ?? 0,
        done: doneRes.count ?? 0,
        errored: erroredRes.count ?? 0,
        retrying: retryingRes.count ?? 0,
      };
      countsByEntity[eid].total = countsByEntity[eid].queued + countsByEntity[eid].processing + countsByEntity[eid].done + countsByEntity[eid].errored + countsByEntity[eid].retrying;
    }
    setVoucherProcessQueue(countsByEntity);
  }, [entities]);

  useEffect(() => { loadVoucherQueue(); }, [loadVoucherQueue]);

  // Poll while there are queued/processing jobs (day-level or voucher-level)
  useEffect(() => {
    const hasActiveDays = Object.values(voucherQueue).some(rows =>
      rows.some(r => r.status === 'queued' || r.status === 'processing')
    );
    const hasActiveVouchers = Object.values(voucherProcessQueue).some(vc =>
      (vc.queued ?? 0) > 0 || (vc.processing ?? 0) > 0 || (vc.retrying ?? 0) > 0
    );
    if (!hasActiveDays && !hasActiveVouchers) return;
    const timer = setInterval(loadVoucherQueue, 5_000);
    return () => clearInterval(timer);
  }, [voucherQueue, voucherProcessQueue, loadVoucherQueue]);

  // ── Handlers ────────────────────────────────────────────────────────

  async function handleSaveToken(entityId) {
    const token = tokenInput[entityId]?.trim();
    if (!token) return;
    setSavingToken(prev => ({ ...prev, [entityId]: true }));
    setTokenResult(prev => ({ ...prev, [entityId]: null }));
    try {
      const res = await callFn('set-employee-token', {
        body: { legal_entity_id: entityId, employee_token: token },
      });
      setTokenResult(prev => ({ ...prev, [entityId]: { valid: res.valid, error: res.error } }));
      if (res.valid) {
        setTokenInput(prev => ({ ...prev, [entityId]: '' }));
        loadEntities();
      }
    } catch (err) {
      setTokenResult(prev => ({ ...prev, [entityId]: { valid: false, error: err.message } }));
    }
    setSavingToken(prev => ({ ...prev, [entityId]: false }));
  }

  async function handleSync(entityId, type) {
    setSyncState(prev => ({
      ...prev,
      [entityId]: { ...(prev[entityId] || {}), [type]: 'loading' },
    }));
    try {
      const data = await callFn('tripletex-bulk-sync', {
        body: { legal_entity_id: entityId, entities: [type] },
      });
      setSyncResults(prev => ({
        ...prev,
        [entityId]: { ...(prev[entityId] || {}), [type]: data.results?.[type] },
      }));
      setSyncState(prev => ({
        ...prev,
        [entityId]: { ...(prev[entityId] || {}), [type]: 'done' },
      }));
    } catch (err) {
      setSyncResults(prev => ({
        ...prev,
        [entityId]: { ...(prev[entityId] || {}), [type]: { synced: 0, errors: 1, message: err.message } },
      }));
      setSyncState(prev => ({
        ...prev,
        [entityId]: { ...(prev[entityId] || {}), [type]: 'error' },
      }));
    }
  }

  async function handleAddAllSubscriptions(entityId) {
    setAddingAllSubs(prev => ({ ...prev, [entityId]: true }));
    setAddSubsResult(prev => ({ ...prev, [entityId]: null }));
    try {
      const existingEvents = new Set(
        (subscriptions[entityId] || []).map(s => s.event_type)
      );
      const missing = STANDARD_EVENTS.filter(e => !existingEvents.has(e));
      if (missing.length === 0) {
        setAddSubsResult(prev => ({ ...prev, [entityId]: { ok: 0, skipped: STANDARD_EVENTS.length } }));
        setAddingAllSubs(prev => ({ ...prev, [entityId]: false }));
        return;
      }
      const data = await callFn('tripletex-manage-subscriptions', {
        body: { legal_entity_id: entityId, events: missing },
      });
      const ok     = data.results?.filter(r => r.success).length ?? 0;
      const failed = data.results?.filter(r => !r.success).length ?? 0;
      setAddSubsResult(prev => ({ ...prev, [entityId]: { ok, failed } }));
      await loadSubscriptions(entityId);
    } catch (err) {
      setAddSubsResult(prev => ({ ...prev, [entityId]: { ok: 0, failed: 1, error: err.message } }));
    }
    setAddingAllSubs(prev => ({ ...prev, [entityId]: false }));
  }

  async function handleDeleteSubscription(entityId, subscriptionId) {
    const key = `${entityId}-${subscriptionId}`;
    setDeletingSub(prev => ({ ...prev, [key]: true }));
    try {
      await callFn('tripletex-manage-subscriptions', {
        method: 'DELETE',
        body: { legal_entity_id: entityId, subscription_id: subscriptionId },
      });
      await loadSubscriptions(entityId);
    } catch (err) {
      console.error('Delete subscription failed:', err);
    }
    setDeletingSub(prev => ({ ...prev, [key]: false }));
  }

  async function handleSaveCashAccounts(entityId, cashId, lossId) {
    setCashAccountSaving(prev => ({ ...prev, [entityId]: true }));
    setCashAccountResult(prev => ({ ...prev, [entityId]: null }));
    const { error } = await supabase
      .from('legal_entities')
      .update({
        cash_account_tripletex_id: cashId || null,
        cash_loss_account_tripletex_id: lossId || null,
      })
      .eq('id', entityId);
    setCashAccountResult(prev => ({ ...prev, [entityId]: error ? 'error' : 'ok' }));
    if (!error) await loadEntities();
    setCashAccountSaving(prev => ({ ...prev, [entityId]: false }));
  }

  async function handleEnqueueVoucherSync(entityId) {
    const dateFrom = voucherDateFrom[entityId];
    const dateTo   = voucherDateTo[entityId];
    if (!dateFrom || !dateTo) return;

    setVoucherEnqueuing(prev => ({ ...prev, [entityId]: true }));

    const days = [];
    let cursor = new Date(dateFrom + 'T00:00:00Z');
    const end = new Date(dateTo + 'T00:00:00Z');
    while (cursor <= end) {
      days.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const rows = days.map(d => ({
      legal_entity_id: entityId,
      sync_date: d,
      status: 'queued',
      created_by: currentUser?.id,
    }));

    const { error } = await supabase
      .from('voucher_sync_queue')
      .upsert(rows, { onConflict: 'legal_entity_id,sync_date', ignoreDuplicates: true });

    if (error) {
      console.error('Failed to enqueue voucher sync:', error);
    } else {
      await loadVoucherQueue();
    }

    setVoucherEnqueuing(prev => ({ ...prev, [entityId]: false }));
  }

  if (!user) return null;

  return (
    <div className={`min-h-screen flex ${bg}`}>
      <Sidebar
        isDark={isDark}
        setIsDark={setIsDark}
        activePage="/enheter"
        entities={entities}
        selectedEntity={entities[0]?.id ?? null}
      />

      {/* ── Main ── */}
      <main className="flex-1 p-8 overflow-y-auto">
        <h2 className="font-bbhbogle text-2xl tracking-[0.1em] mb-8">Enheter</h2>

        {loading && (
          <p className="font-mono text-xs text-slate-500">Laster…</p>
        )}

        {!loading && entities.length === 0 && (
          <p className="font-mono text-xs text-slate-500">Ingen enheter funnet.</p>
        )}

        <div className="space-y-6 max-w-3xl">
          {entities.map(entity => {
            const integration = entity.tripletex_integration;
            const subs         = subscriptions[entity.id] || [];
            const syncSt       = syncState[entity.id]   || {};
            const syncRes      = syncResults[entity.id] || {};
            const isAdmin      = entity.role === 'admin';

            return (
              <div key={entity.id} className={`rounded-xl border ${card} overflow-hidden`}>
                {/* Entity header */}
                <div className={`px-6 py-5 border-b ${section} flex items-center justify-between`}>
                  <div>
                    <h3 className="font-bbhbogle text-lg tracking-[0.08em]">{entity.name}</h3>
                    <p className={`font-mono text-[11px] mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Org.nr {entity.organization_number?.toLocaleString('nb-NO') ?? '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusDot status={integration?.token_status} />
                    <StatusLabel status={integration?.token_status} isDark={isDark} />
                  </div>
                </div>

                <div className="divide-y divide-inherit" style={{ borderColor: isDark ? 'rgb(30,41,59)' : 'rgb(226,232,240)' }}>

                  {/* ── API-nøkkel ── */}
                  {isAdmin && (
                    <div className="px-6 py-5">
                      <p className={`font-mono text-[11px] uppercase tracking-widest mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        API-nøkkel
                      </p>
                      <div className="flex gap-3 items-end">
                        <input
                          type="password"
                          value={tokenInput[entity.id] ?? ''}
                          onChange={e => setTokenInput(prev => ({ ...prev, [entity.id]: e.target.value }))}
                          placeholder="Ny employee token…"
                          className={`flex-1 font-mono text-xs pb-1 outline-none transition-colors ${inputCls}`}
                          onKeyDown={e => e.key === 'Enter' && handleSaveToken(entity.id)}
                        />
                        <button
                          onClick={() => handleSaveToken(entity.id)}
                          disabled={savingToken[entity.id] || !tokenInput[entity.id]?.trim()}
                          className={`font-mono text-xs px-4 py-1.5 rounded transition-colors disabled:opacity-40 ${btnPrimary}`}
                        >
                          {savingToken[entity.id] ? 'Lagrer…' : 'Lagre'}
                        </button>
                      </div>
                      {tokenResult[entity.id] && (
                        <p className={`font-mono text-[11px] mt-2 ${tokenResult[entity.id].valid ? 'text-emerald-500' : 'text-red-400'}`}>
                          {tokenResult[entity.id].valid
                            ? '✓ Nøkkel godkjent og lagret'
                            : `✗ ${tokenResult[entity.id].error || 'Ugyldig nøkkel'}`}
                        </p>
                      )}
                      {integration?.last_verified_at && (
                        <p className={`font-mono text-[11px] mt-1.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                          Sist bekreftet {new Date(integration.last_verified_at).toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      )}
                      {integration?.last_error && integration.token_status === 'invalid' && (
                        <p className="font-mono text-[11px] mt-1 text-red-400">
                          {integration.last_error}
                        </p>
                      )}
                    </div>
                  )}

                  {/* ── Bulksynkronisering ── */}
                  {isAdmin && integration?.token_status === 'valid' && (
                    <div className="px-6 py-5">
                      <p className={`font-mono text-[11px] uppercase tracking-widest mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Bulksynkronisering
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {SYNC_TYPES.map(({ key, label }) => {
                          const state  = syncSt[key];
                          const result = syncRes[key];
                          return (
                            <div key={key} className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleSync(entity.id, key)}
                                disabled={state === 'loading'}
                                className={`font-mono text-xs px-3 py-1.5 rounded transition-colors disabled:opacity-40 ${btnSmall}`}
                              >
                                {state === 'loading' ? `${label}…` : label}
                              </button>
                              {(state === 'done' || state === 'error') && result && (
                                <SyncResult result={result} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Bilagssynkronisering ── */}
                  {isAdmin && integration?.token_status === 'valid' && (() => {
                    // Day-level queue
                    const queueRows = voucherQueue[entity.id] || [];
                    const dayQueued     = queueRows.filter(r => r.status === 'queued').length;
                    const dayProcessing = queueRows.filter(r => r.status === 'processing').length;
                    const dayDone       = queueRows.filter(r => r.status === 'done').length;
                    const dayErrored    = queueRows.filter(r => r.status === 'error').length;
                    const dayTotal      = queueRows.length;
                    const dayHasActive  = dayQueued > 0 || dayProcessing > 0;

                    // Voucher-level counts (already aggregated server-side)
                    const vc        = voucherProcessQueue[entity.id] || {};
                    const vQueued     = vc.queued     ?? 0;
                    const vProcessing = vc.processing ?? 0;
                    const vDone       = vc.done       ?? 0;
                    const vErrored    = vc.errored    ?? 0;
                    const vRetrying   = vc.retrying   ?? 0;
                    const vTotal      = vc.total      ?? 0;
                    const vHasActive  = vQueued > 0 || vProcessing > 0 || vRetrying > 0;

                    return (
                      <div className="px-6 py-5">
                        <p className={`font-mono text-[11px] uppercase tracking-widest mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Bilagssynkronisering
                        </p>
                        <div className="flex flex-wrap items-end gap-3">
                          <label className="flex flex-col gap-1">
                            <span className={`font-mono text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Fra</span>
                            <input
                              type="date"
                              value={voucherDateFrom[entity.id] ?? ''}
                              onChange={e => setVoucherDateFrom(prev => ({ ...prev, [entity.id]: e.target.value }))}
                              className={`font-mono text-xs px-2 py-1.5 rounded border outline-none transition-colors ${isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'}`}
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className={`font-mono text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Til</span>
                            <input
                              type="date"
                              value={voucherDateTo[entity.id] ?? ''}
                              onChange={e => setVoucherDateTo(prev => ({ ...prev, [entity.id]: e.target.value }))}
                              className={`font-mono text-xs px-2 py-1.5 rounded border outline-none transition-colors ${isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'}`}
                            />
                          </label>
                          <button
                            onClick={() => handleEnqueueVoucherSync(entity.id)}
                            disabled={voucherEnqueuing[entity.id] || !voucherDateFrom[entity.id] || !voucherDateTo[entity.id]}
                            className={`font-mono text-xs px-4 py-1.5 rounded transition-colors disabled:opacity-40 ${btnPrimary}`}
                          >
                            {voucherEnqueuing[entity.id] ? 'Legger i kø…' : 'Legg i synkroniseringskø'}
                          </button>
                        </div>

                        {/* Day-level progress */}
                        {dayTotal > 0 && (
                          <div className="mt-3">
                            {dayHasActive && (
                              <div className="mb-2">
                                <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                                  <div
                                    className="h-full bg-slate-500 rounded-full transition-all duration-300"
                                    style={{ width: `${((dayDone + dayErrored) / dayTotal) * 100}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            <p className={`font-mono text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              {dayDone} av {dayTotal} dager listet
                              {dayErrored > 0 && <span className="text-red-400"> ({dayErrored} feil)</span>}
                              {dayHasActive && <span className="text-amber-400"> — {dayQueued + dayProcessing} gjenstår</span>}
                            </p>
                          </div>
                        )}

                        {/* Voucher-level progress */}
                        {vTotal > 0 && (
                          <div className="mt-2">
                            {(vHasActive || vDone > 0) && (
                              <div className="mb-2">
                                <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                                  <div
                                    className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                                    style={{ width: `${((vDone + vErrored) / vTotal) * 100}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            <p className={`font-mono text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              {vDone} av {vTotal} bilag synkronisert
                              {vErrored > 0 && <span className="text-red-400"> ({vErrored} feilet)</span>}
                              {vRetrying > 0 && <span className="text-amber-400"> ({vRetrying} prøver på nytt)</span>}
                              {vProcessing > 0 && <span className="text-blue-400"> ({vProcessing} behandles)</span>}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Kontantavstemming ── */}
                  {isAdmin && integration?.token_status === 'valid' && (() => {
                    const accounts = entityAccounts[entity.id] || [];
                    const saving = cashAccountSaving[entity.id];
                    const result = cashAccountResult[entity.id];
                    const [cashId, setCashId] = [
                      entity.cash_account_tripletex_id?.toString() ?? '',
                      (v) => {
                        setEntities(prev => prev.map(e =>
                          e.id === entity.id ? { ...e, cash_account_tripletex_id: v ? Number(v) : null } : e
                        ));
                      },
                    ];
                    const [lossId, setLossId] = [
                      entity.cash_loss_account_tripletex_id?.toString() ?? '',
                      (v) => {
                        setEntities(prev => prev.map(e =>
                          e.id === entity.id ? { ...e, cash_loss_account_tripletex_id: v ? Number(v) : null } : e
                        ));
                      },
                    ];
                    return (
                      <div className="px-6 py-5">
                        <p className={`font-mono text-[11px] uppercase tracking-widest mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Kontantavstemming
                        </p>
                        {accounts.length === 0 ? (
                          <p className={`font-mono text-[11px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                            Ingen kontoer synkronisert. Kjør bulksynkronisering for Kontoer først.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-4 items-end">
                            <label className="flex flex-col gap-1">
                              <span className={`font-mono text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Kontantkonto</span>
                              <select
                                value={cashId}
                                onChange={e => setCashId(e.target.value)}
                                className={`font-mono text-xs px-2 py-1.5 rounded border outline-none transition-colors ${isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'}`}
                              >
                                <option value="">— Velg konto —</option>
                                {accounts.map(a => (
                                  <option key={a.tripletex_id} value={a.tripletex_id}>
                                    {a.number} {a.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className={`font-mono text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Tapskonto (kontantsvinn)</span>
                              <select
                                value={lossId}
                                onChange={e => setLossId(e.target.value)}
                                className={`font-mono text-xs px-2 py-1.5 rounded border outline-none transition-colors ${isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'}`}
                              >
                                <option value="">— Velg konto —</option>
                                {accounts.map(a => (
                                  <option key={a.tripletex_id} value={a.tripletex_id}>
                                    {a.number} {a.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <button
                              onClick={() => handleSaveCashAccounts(
                                entity.id,
                                entity.cash_account_tripletex_id,
                                entity.cash_loss_account_tripletex_id,
                              )}
                              disabled={saving}
                              className={`font-mono text-xs px-4 py-1.5 rounded transition-colors disabled:opacity-40 ${btnPrimary}`}
                            >
                              {saving ? 'Lagrer…' : 'Lagre'}
                            </button>
                            {result && (
                              <span className={`font-mono text-[11px] ${result === 'ok' ? 'text-emerald-500' : 'text-red-400'}`}>
                                {result === 'ok' ? '✓ Lagret' : '✗ Feil ved lagring'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Hendelsesabonnementer ── */}
                  {isAdmin && integration?.token_status === 'valid' && (
                    <div className="px-6 py-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className={`font-mono text-[11px] uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          Hendelsesabonnementer
                        </p>
                        <button
                          onClick={() => handleAddAllSubscriptions(entity.id)}
                          disabled={addingAllSubs[entity.id]}
                          className={`font-mono text-[11px] px-3 py-1 rounded transition-colors disabled:opacity-40 ${btnSmall}`}
                        >
                          {addingAllSubs[entity.id] ? 'Legger til…' : '+ Legg til standard'}
                        </button>
                      </div>

                      {addSubsResult[entity.id] && (
                        <p className={`font-mono text-[11px] mb-3 ${addSubsResult[entity.id].failed > 0 ? 'text-amber-400' : 'text-emerald-500'}`}>
                          {addSubsResult[entity.id].skipped
                            ? '✓ Alle standard abonnementer er allerede aktive'
                            : addSubsResult[entity.id].failed > 0
                              ? `✓ ${addSubsResult[entity.id].ok} lagt til, ${addSubsResult[entity.id].failed} feilet`
                              : `✓ ${addSubsResult[entity.id].ok} abonnementer lagt til`}
                        </p>
                      )}

                      {loadingSubs[entity.id] ? (
                        <p className={`font-mono text-[11px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Laster…</p>
                      ) : subs.length === 0 ? (
                        <p className={`font-mono text-[11px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                          Ingen aktive abonnementer.
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {subs.map(sub => {
                            const delKey = `${entity.id}-${sub.subscription_id}`;
                            return (
                              <div
                                key={sub.id}
                                className={`flex items-center justify-between px-3 py-1.5 rounded ${isDark ? 'bg-slate-800/60' : 'bg-slate-50'}`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                  <span className="font-mono text-[11px] truncate">{sub.event_type}</span>
                                </div>
                                <button
                                  onClick={() => handleDeleteSubscription(entity.id, sub.subscription_id)}
                                  disabled={deletingSub[delKey]}
                                  className={`font-mono text-xs ml-2 px-1.5 rounded transition-colors shrink-0 ${isDark ? 'text-slate-600 hover:text-red-400' : 'text-slate-400 hover:text-red-500'} disabled:opacity-40`}
                                  title="Slett abonnement"
                                >
                                  {deletingSub[delKey] ? '…' : '×'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Non-admin read-only view */}
                  {!isAdmin && (
                    <div className="px-6 py-4">
                      <p className={`font-mono text-[11px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                        Du har lesetilgang til denne enheten.
                      </p>
                    </div>
                  )}

                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
